import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { 
  Bus, ArrowLeft, Ticket, Clock, MapPin, User, 
  Calendar, QrCode, AlertCircle, CheckCircle, XCircle,
  ChevronRight, Search, Star, UserPlus, Download, Navigation, Share2, Wallet, Smartphone
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import api from '../../utils/api';
import { ShareTripButton } from '../../components/bus/ShareTripModal';

const BusMyTickets = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [filter, setFilter] = useState('all');

  // Refund flow state
  const [cancelTarget, setCancelTarget] = useState(null);  // {bookingId, ticketCode}
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [refundForm, setRefundForm] = useState({ refund_method: 'wallet', mobile_money_phone: '', reason: '' });
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('tekstar_token');
    if (!token) {
      toast({
        title: 'Login Required',
        description: 'Please login to view your tickets',
        variant: 'destructive'
      });
      navigate('/login', { state: { returnTo: '/bus/tickets' } });
      return;
    }
    
    fetchTickets();
  }, [navigate]);

  const fetchTickets = async () => {
    try {
      const response = await api.get('/bus/bookings/my');
      setTickets(response.data.bookings || []);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      toast({
        title: 'Error',
        description: 'Failed to load tickets',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const openCancel = async (booking) => {
    setCancelTarget(booking);
    setPreview(null);
    setPreviewLoading(true);
    setRefundForm({ refund_method: 'wallet', mobile_money_phone: '', reason: '' });
    try {
      const { data } = await api.get(`/bus/bookings/${booking.id}/refund-preview`);
      setPreview(data);
    } catch (err) {
      toast({ title: 'Cannot cancel', description: err.response?.data?.detail || 'Failed', variant: 'destructive' });
      setCancelTarget(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleCancelBooking = async () => {
    if (!cancelTarget) return;
    if (refundForm.refund_method === 'mobile_money' && !refundForm.mobile_money_phone) {
      toast({ title: 'Phone required', description: 'Mobile Money refund needs a phone number', variant: 'destructive' });
      return;
    }
    setCancelling(true);
    try {
      await api.post(`/bus/bookings/${cancelTarget.id}/cancel`, {
        reason: refundForm.reason || 'Customer requested cancellation',
        refund_method: refundForm.refund_method,
        mobile_money_phone: refundForm.refund_method === 'mobile_money' ? refundForm.mobile_money_phone : null,
      });
      toast({
        title: 'Booking cancelled',
        description: preview?.refundAmount > 0
          ? `K${preview.refundAmount.toFixed(2)} refund queued — admin will approve shortly.`
          : 'No refund applies for this cancellation window.',
      });
      setCancelTarget(null);
      fetchTickets();
    } catch (error) {
      toast({
        title: 'Cancellation failed',
        description: error.response?.data?.detail || 'Failed to cancel booking',
        variant: 'destructive'
      });
    } finally {
      setCancelling(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 text-green-700';
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'completed': return 'bg-blue-100 text-blue-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed': return <CheckCircle className="h-4 w-4" />;
      case 'cancelled': return <XCircle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  const filteredTickets = tickets.filter(ticket => {
    if (filter === 'all') return true;
    return ticket.status === filter;
  });

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/bus')}>
              <ArrowLeft className="h-6 w-6" />
            </button>
            <div>
              <h1 className="text-xl font-bold">My Bus Tickets</h1>
              <p className="text-blue-100 text-sm">{tickets.length} ticket(s)</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4">
        {/* Filters */}
        <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
          {['all', 'confirmed', 'pending', 'completed', 'cancelled'].map((f) => (
            <Button
              key={f}
              variant={filter === f ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(f)}
              className={filter === f ? 'bg-blue-600' : ''}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </Button>
          ))}
        </div>

        {/* Tickets List */}
        {filteredTickets.length === 0 ? (
          <Card className="p-8 text-center">
            <Ticket className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">No Tickets Found</h3>
            <p className="text-gray-500 mb-4">
              {filter === 'all' 
                ? "You haven't booked any bus tickets yet"
                : `No ${filter} tickets`
              }
            </p>
            <Link to="/bus">
              <Button className="bg-blue-600 hover:bg-blue-700">
                <Search className="h-4 w-4 mr-2" />
                Search Buses
              </Button>
            </Link>
          </Card>
        ) : (
          <div className="space-y-4" data-testid="tickets-list">
            {filteredTickets.map((ticket) => (
              <Card 
                key={ticket.id}
                className="overflow-hidden hover:shadow-md transition-all"
                data-testid={`ticket-${ticket.id}`}
              >
                {/* Ticket Header */}
                <div className="bg-blue-600 text-white p-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Ticket className="h-5 w-5" />
                    <span className="font-mono font-bold">{ticket.ticketCode}</span>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(ticket.status)}`}>
                    {getStatusIcon(ticket.status)}
                    <span className="ml-1">{ticket.status}</span>
                  </span>
                </div>

                <div className="p-4">
                  {/* Route & Time */}
                  <div className="flex items-center gap-4 mb-4">
                    <div className="text-center">
                      <p className="text-xl font-bold">{ticket.trip?.departureTime || '--:--'}</p>
                      <p className="text-sm text-gray-500">{ticket.boarding?.city}</p>
                    </div>
                    <div className="flex-1 flex items-center gap-2">
                      <div className="flex-1 border-t-2 border-dashed border-gray-300" />
                      <Bus className="h-5 w-5 text-blue-600" />
                      <div className="flex-1 border-t-2 border-dashed border-gray-300" />
                    </div>
                    <div className="text-center">
                      <p className="text-xl font-bold">{ticket.trip?.arrivalTime || '--:--'}</p>
                      <p className="text-sm text-gray-500">{ticket.alighting?.city}</p>
                    </div>
                  </div>

                  {/* Details */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                    <div>
                      <p className="text-gray-500 flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        Date
                      </p>
                      <p className="font-medium">{formatDate(ticket.trip?.date)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 flex items-center gap-1">
                        <Bus className="h-4 w-4" />
                        Operator
                      </p>
                      <p className="font-medium">{ticket.operatorName}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 flex items-center gap-1">
                        <User className="h-4 w-4" />
                        Passengers
                      </p>
                      <p className="font-medium">{ticket.passengers?.length || 0}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Seats</p>
                      <p className="font-medium">
                        {ticket.passengers?.map(p => p.seat).join(', ')}
                      </p>
                    </div>
                  </div>

                  {/* QR Code (collapsed) */}
                  {ticket.qrCode && ticket.status !== 'cancelled' && (
                    <details className="border-t pt-4">
                      <summary className="cursor-pointer text-blue-600 font-medium flex items-center gap-2">
                        <QrCode className="h-4 w-4" />
                        Show QR Code
                      </summary>
                      <div className="mt-4 text-center">
                        <img 
                          src={ticket.qrCode} 
                          alt="Ticket QR" 
                          className="mx-auto w-32 h-32"
                        />
                        <p className="text-sm text-gray-500 mt-2">Show when boarding</p>
                      </div>
                    </details>
                  )}

                  {/* Cancellation Info */}
                  {ticket.cancellation && (
                    <div className="mt-4 p-3 bg-red-50 rounded-lg">
                      <p className="text-sm text-red-600">
                        <AlertCircle className="h-4 w-4 inline mr-1" />
                        Cancelled: {ticket.cancellation.reason}
                      </p>
                      {ticket.cancellation.refundAmount > 0 && (
                        <p className="text-sm text-gray-600 mt-1">
                          Refund: K{ticket.cancellation.refundAmount} ({ticket.cancellation.refundPercent}%)
                          - {ticket.cancellation.refundStatus}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t">
                    <div className="flex-1 min-w-[100px]">
                      <p className="text-sm text-gray-500">Total Paid</p>
                      <p className="text-xl font-bold text-blue-600">
                        K{ticket.pricing?.total}
                      </p>
                    </div>
                    
                    {/* Download ticket button */}
                    {ticket.status !== 'cancelled' && (
                      <a 
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/bus/bookings/${ticket.id}/ticket.pdf`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Button 
                          size="sm"
                          variant="outline"
                          className="text-blue-600"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          PDF
                        </Button>
                      </a>
                    )}
                    
                    {/* Share button */}
                    {ticket.status !== 'cancelled' && (
                      <ShareTripButton booking={ticket} variant="button" />
                    )}
                    
                    {/* Track bus button for confirmed trips */}
                    {ticket.status === 'confirmed' && ticket.tripId && (
                      <Link to={`/bus/track/${ticket.tripId}`}>
                        <Button 
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <Navigation className="h-4 w-4 mr-1" />
                          Track
                        </Button>
                      </Link>
                    )}
                    
                    {/* Review button for completed trips */}
                    {ticket.status === 'completed' && !ticket.reviewed && (
                      <Link to={`/bus/review/${ticket.id}`}>
                        <Button 
                          size="sm"
                          className="bg-yellow-500 hover:bg-yellow-600"
                        >
                          <Star className="h-4 w-4 mr-1" />
                          Rate
                        </Button>
                      </Link>
                    )}
                    
                    {/* Transfer button for confirmed tickets */}
                    {ticket.status === 'confirmed' && (
                      <Link to={`/bus/transfer/${ticket.id}`}>
                        <Button 
                          variant="outline"
                          size="sm"
                        >
                          <UserPlus className="h-4 w-4 mr-1" />
                          Transfer
                        </Button>
                      </Link>
                    )}
                    
                    {ticket.status === 'confirmed' && (
                      <Button 
                        variant="outline"
                        size="sm"
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => openCancel(ticket)}
                        data-testid={`bus-cancel-btn-${ticket.id}`}
                      >
                        Cancel
                      </Button>
                    )}
                    {ticket.status === 'cancelled' && ticket.cancellation && (
                      <span
                        className={`text-xs px-2 py-1 rounded-full font-medium ${
                          ticket.cancellation.refundStatus === 'processed'
                            ? 'bg-green-100 text-green-700'
                            : ticket.cancellation.refundStatus === 'processed_queued'
                            ? 'bg-blue-100 text-blue-700'
                            : ticket.cancellation.refundStatus === 'denied'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-amber-100 text-amber-700'
                        }`}
                        data-testid={`bus-refund-status-${ticket.id}`}
                      >
                        {ticket.cancellation.refundStatus === 'processed' && `✓ K${ticket.cancellation.refundAmount?.toFixed(2)} in wallet`}
                        {ticket.cancellation.refundStatus === 'processed_queued' && `→ K${ticket.cancellation.refundAmount?.toFixed(2)} MM payout queued`}
                        {ticket.cancellation.refundStatus === 'pending' && `⏳ K${ticket.cancellation.refundAmount?.toFixed(2)} pending`}
                        {ticket.cancellation.refundStatus === 'denied' && 'Refund denied'}
                        {ticket.cancellation.refundStatus === 'not_applicable' && 'No refund'}
                      </span>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Book New Trip CTA */}
        <Card className="mt-6 p-4 bg-gradient-to-r from-blue-500 to-indigo-500 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-bold mb-1">Need to travel again?</h3>
              <p className="text-blue-100 text-sm">Book your next bus trip now</p>
            </div>
            <Link to="/bus">
              <Button className="bg-white text-blue-600 hover:bg-blue-50">
                Search Buses
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
          </div>
        </Card>
      </div>

      {/* Cancel-with-refund-preview dialog */}
      <Dialog open={!!cancelTarget} onOpenChange={(v) => !v && !cancelling && setCancelTarget(null)}>
        <DialogContent className="sm:max-w-md" data-testid="bus-cancel-dialog">
          <DialogHeader>
            <DialogTitle>Cancel booking</DialogTitle>
            <DialogDescription>{cancelTarget?.ticketCode} — review your refund before confirming.</DialogDescription>
          </DialogHeader>

          {previewLoading && <p className="text-sm text-gray-500">Calculating refund…</p>}

          {preview && (
            <div className="space-y-4">
              <div className="bg-gradient-to-br from-pink-50 to-pink-100 border border-pink-200 rounded-lg p-4">
                <p className="text-xs text-pink-700 font-medium uppercase tracking-wide">Refund estimate</p>
                <p className="text-3xl font-bold text-pink-700 mt-1" data-testid="bus-refund-amount">K{preview.refundAmount.toFixed(2)}</p>
                <p className="text-xs text-pink-900 mt-1">
                  {preview.refundPercent}% of K{preview.totalPaid.toFixed(2)} paid · {preview.hoursUntilDeparture.toFixed(1)}h before departure
                </p>
                {preview.refundAmount === 0 && (
                  <p className="text-xs text-red-700 mt-2 bg-red-50 p-2 rounded">
                    ⚠ Less than 6h to departure — no refund applies. You can still cancel to free your seat.
                  </p>
                )}
              </div>

              {/* Additive refund breakdown */}
              <div className="text-xs text-gray-600 space-y-1 border border-gray-200 rounded-lg p-3" data-testid="bus-refund-breakdown">
                <div className="flex justify-between">
                  <span>Operator fare refund ({preview.refundPercent}%)</span>
                  <span className="font-medium">K{(preview.operatorRefund ?? preview.refundAmount).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Processing fee</span>
                  <span className="font-medium" data-testid="bus-refund-platform-fee">
                    {preview.platformFeeRefundable
                      ? `+ K${(preview.platformFee ?? 0).toFixed(2)} refunded`
                      : `K${(preview.platformFee ?? 0).toFixed(2)} non-refundable`}
                  </span>
                </div>
              </div>

              {preview.refundAmount > 0 && (
                <div>
                  <label className="text-xs font-medium block mb-1">Refund to</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setRefundForm({ ...refundForm, refund_method: 'wallet' })}
                      className={`p-3 rounded-md border text-left text-xs ${refundForm.refund_method === 'wallet' ? 'bg-pink-50 border-pink-500' : 'bg-white border-gray-300'}`}
                      data-testid="bus-refund-wallet-btn"
                    >
                      <Wallet className="h-4 w-4 mb-1 text-pink-600" />
                      <p className="font-semibold">Wallet credit</p>
                      <p className="text-[10px] text-gray-500">Instant on approval</p>
                    </button>
                    <button
                      onClick={() => setRefundForm({ ...refundForm, refund_method: 'mobile_money' })}
                      className={`p-3 rounded-md border text-left text-xs ${refundForm.refund_method === 'mobile_money' ? 'bg-pink-50 border-pink-500' : 'bg-white border-gray-300'}`}
                      data-testid="bus-refund-mm-btn"
                    >
                      <Smartphone className="h-4 w-4 mb-1 text-pink-600" />
                      <p className="font-semibold">Mobile Money</p>
                      <p className="text-[10px] text-gray-500">Within 24h</p>
                    </button>
                  </div>
                </div>
              )}

              {refundForm.refund_method === 'mobile_money' && preview.refundAmount > 0 && (
                <div>
                  <label className="text-xs font-medium block mb-1">Mobile Money phone *</label>
                  <Input
                    type="tel"
                    placeholder="+260 977 123 456"
                    value={refundForm.mobile_money_phone}
                    onChange={(e) => setRefundForm({ ...refundForm, mobile_money_phone: e.target.value })}
                    data-testid="bus-refund-mm-phone-input"
                  />
                </div>
              )}

              <div>
                <label className="text-xs font-medium block mb-1">Reason (optional)</label>
                <textarea
                  value={refundForm.reason}
                  onChange={(e) => setRefundForm({ ...refundForm, reason: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  rows={2}
                  placeholder="E.g. Plans changed"
                  data-testid="bus-refund-reason-input"
                />
              </div>

              <div className="flex gap-2 pt-1">
                <Button variant="outline" className="flex-1" onClick={() => setCancelTarget(null)} disabled={cancelling}>
                  Keep booking
                </Button>
                <Button
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  onClick={handleCancelBooking}
                  disabled={cancelling}
                  data-testid="bus-cancel-confirm-btn"
                >
                  {cancelling ? 'Cancelling…' : 'Confirm cancel'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BusMyTickets;
